import { useAuth } from "../context/AuthContext";
import { useBillingInfo } from "../data/useBillingInfo";
import { ROUTE } from "../routes";
import { BillingInfo } from "../types/billingInfo";

/**
 * The four modules in sidebar order, each gated on BOTH the tenant's
 * subscription flag and the user's own view permission — the same two checks
 * the Sidebar uses to enable each nav item.
 */
export const MODULES = [
  {
    route: ROUTE.PROFILES,
    flag: "profiles_enabled",
    permission: "profile.view",
  },
  {
    route: ROUTE.APPLICATIONS,
    flag: "applications_enabled",
    permission: "mandate.view",
  },
  {
    route: ROUTE.CHECKS,
    flag: "screening_enabled",
    permission: "screening.view",
  },
  {
    route: ROUTE.MONITORING,
    flag: "monitoring_enabled",
    permission: "monitoring.view",
  },
] as const;

/**
 * Where to send a signed-in user with no specific return URL: the first
 * module (Profiles > Applications > Screening > Monitoring) that's both on
 * for the tenant and viewable by this user. Checking the permission, not just
 * the billing flag, is what stops a user without e.g. `profile.view` landing
 * on Profiles, whose list request then 403s and logs them out.
 *
 * With no viewable module, admins go to Settings and everyone else to their
 * own Profile page — never Settings for a non-admin, since Settings bounces
 * non-admins back to ROOT, which would loop through Login straight back here.
 *
 * UX routing only: the backend still enforces every permission.
 */
export const useDefaultLandingRoute = (enabled?: boolean) => {
  const { hasPermission, isAdmin, isLoadingUserDetails } = useAuth();
  const { data: billingData, isLoading: isLoadingBilling } =
    useBillingInfo(enabled);

  const billing = billingData?.data?.results?.[0] as BillingInfo | undefined;
  const firstModule = MODULES.find(
    ({ flag, permission }) =>
      Boolean(billing?.[flag]) && hasPermission(permission)
  );

  return {
    defaultRoute:
      firstModule?.route ?? (isAdmin ? ROUTE.SETTINGS : ROUTE.PROFILE),
    isLoading: isLoadingUserDetails || isLoadingBilling,
  };
};
